import React, { FunctionComponent, ReactNode } from 'react'
import { useIntl } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import { BodyText, Size as BodyTextSize } from '../../atoms/bodyText/BodyText'
import { AddIcon } from '../../atoms/icons/AddIcon'
import { ChevronIcon, ChevronIconDirection } from '../../atoms/icons/ChevronIcon'
import { SafetyPlanIcon } from '../../atoms/icons/SafetyPlanIcon'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { Title, Size as TitleSize } from '../../atoms/title/Title'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export type SafetyPlanRowProps = {
  createdDate?: ReactNode
  createdBy?: ReactNode
  /** The plan hasn't been shared yet; createdDate is when it was last auto-saved. */
  isDraft?: boolean
  onPress: () => void
  testID?: string
}

const Row = styled(PressableOpacity)<{ theme: ThemeType; $hasPlan: boolean }>(
  ({ theme: { colors, spacing }, $hasPlan }) => ({
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing['40px'],
    padding: spacing['16px'],
    borderRadius: spacing['16px'],
    backgroundColor: $hasPlan ? colors.backgroundElement : colors.backgroundPrimary,
    borderWidth: $hasPlan ? 1 : 1.5,
    borderStyle: $hasPlan ? 'solid' : 'dashed',
    borderColor: colors.borderDefault,
  }),
)

const Details = styled.View(({ theme: { spacing } }) => ({
  flex: 1,
  gap: spacing['4px'],
}))

const TitleRow = styled.View(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: spacing['8px'],
}))

export const SafetyPlanRow: FunctionComponent<SafetyPlanRowProps> = ({
  createdDate,
  createdBy,
  isDraft,
  onPress,
  testID,
}) => {
  const { colors } = useTheme() as ThemeType
  const { formatMessage } = useIntl()
  const hasPlan = !!createdDate
  const hoverColor = hasPlan ? colors.backgroundSection : colors.backgroundSecondary
  const title = formatMessage({ defaultMessage: 'Safety plan', description: 'Title of the client safety plan row' })

  return (
    <Row
      $hasPlan={hasPlan}
      onPress={onPress}
      pressedOpacity={1}
      pressedBackgroundColor={hoverColor}
      hoveredBackgroundColor={hoverColor}
      accessibilityRole='button'
      accessibilityLabel={
        !hasPlan
          ? formatMessage({
              defaultMessage: 'Create safety plan',
              description: 'Accessibility label to create a new safety plan',
            })
          : isDraft
            ? formatMessage({
                defaultMessage: 'Continue safety plan draft',
                description: 'Accessibility label to resume editing an unshared safety plan draft',
              })
            : formatMessage({
                defaultMessage: 'View safety plan',
                description: 'Accessibility label to open safety plan',
              })
      }
      testID={testID ?? tID('SafetyPlanRow')}
    >
      <Details>
        <TitleRow>
          <SafetyPlanIcon size={24} fillColor={colors.iconDefault} />
          <Title text={title} size={TitleSize.XSMALL} color={colors.textPrimary} />
        </TitleRow>
        {hasPlan ? (
          <BodyText
            testID={tID('SafetyPlanRow-details')}
            text={
              isDraft
                ? formatMessage(
                    {
                      defaultMessage: 'Draft · Auto-saved {date}',
                      description: 'The client safety plan is an unshared draft',
                    },
                    { date: createdDate },
                  )
                : createdBy
                  ? formatMessage(
                      {
                        defaultMessage: 'Created {date} • {author}',
                        description: 'When and by whom the client safety plan was created',
                      },
                      {
                        date: createdDate,
                        author: <BodyText text={createdBy} size={BodyTextSize.SMALL} color={colors.textPrimary} />,
                      },
                    )
                  : formatMessage(
                      { defaultMessage: 'Created {date}', description: 'When the client safety plan was created' },
                      { date: createdDate },
                    )
            }
            size={BodyTextSize.SMALL}
            color={colors.textSecondary}
          />
        ) : (
          <BodyText
            testID={tID('SafetyPlanRow-details')}
            text={formatMessage({
              defaultMessage: 'No plan created',
              description: 'Shown in the safety plan row when the client has no safety plan',
            })}
            size={BodyTextSize.SMALL}
            color={colors.textSecondary}
          />
        )}
      </Details>
      {hasPlan ? (
        <ChevronIcon size={24} fillColor={colors.iconDefault} direction={ChevronIconDirection.RIGHT} />
      ) : (
        <AddIcon width={24} fillColor={colors.iconActive} />
      )}
    </Row>
  )
}
